import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StyledDataGrid from '../../../components/StyledDataGrid';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import WorkspacePage from '../../../components/WorkspacePage';
import { fetchProjects } from '../../../utils/api';
import { formatDateInAppTimeZone } from '../../../utils/appDateTime';
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from '../../../constants/routes';
import { useAuth } from '../../../components/AuthProvider';

const ProjectManagement = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({ projectName: '', irbNum: '' });

  const loadProjects = async () => {
    setLoading(true);
    try {
      const data = await fetchProjects(searchParams);
      // The API may return an array directly based on the document provided.
      setProjects(Array.isArray(data) ? data : (data?.content || []));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Initial load

  const handleSearch = () => {
    loadProjects();
  };

  const columns = [
    {
      field: 'irbNum',
      headerName: 'IRB Number',
      width: 150,
      renderCell: (params) => params.value || 'N/A'
    },
    { 
      field: 'projectName', 
      headerName: 'Project Name', 
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.row.description || "No description available"} placement="top" arrow>
          <span style={{ cursor: 'pointer', display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Button onClick={() => navigate(BIOBANK_SUB_ROUTES.PROJECT_MANAGEMENT_DETAILS(params.row.projectId))}>{params.value}</Button>
          </span>
        </Tooltip>
      )
    },
    { field: 'institution', headerName: 'Institution', flex: 1 },
    { field: 'piName', headerName: 'PI Name', width: 200 },
    { 
      field: 'createdAt', 
      headerName: 'Create Date', 
      width: 150,
      renderCell: (params) => {
        const value = params.row?.createdAt || params.value;
        if (!value) return 'N/A';
        return formatDateInAppTimeZone(value, value);
      }
    }
  ];

  return (
    <WorkspacePage>
      <PageHeader
        title="Project Management"
        actions={isAdmin ? (
          <Button
            variant="contained"
            onClick={() => navigate(BIOBANK_SUB_ROUTES.PROJECT_MANAGEMENT_ADD)}
            sx={{
              bgcolor: '#500000',
              color: '#fff',
              fontWeight: 600,
              height: "40px",
              px: 3,
              fontSize: "14px",
              whiteSpace: "nowrap",
              '&:hover': { bgcolor: '#3a0000' }
            }}
            data-tour-id="tour-create-project"
          >
            Create Project
          </Button>
        ) : null}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1, alignItems: 'center', flexWrap: 'wrap' }} data-tour-id="tour-project-search">
        <SearchBar
          placeholder="IRB start with"
          value={searchParams.irbNum}
          onChange={(e) => setSearchParams({ ...searchParams, irbNum: e.target.value })}
          onClear={() => setSearchParams({ ...searchParams, irbNum: '' })}
          width={250}
        />
        <SearchBar
          placeholder="Project name contains"
          value={searchParams.projectName}
          onChange={(e) => setSearchParams({ ...searchParams, projectName: e.target.value })}
          onClear={() => setSearchParams({ ...searchParams, projectName: '' })}
          width={250}
        />
        <Box sx={{ display: 'flex', gap: 2, ml: { sm: 'auto' } }}>
          <Button 
            variant="contained" 
            onClick={handleSearch}
            sx={{ 
              bgcolor: '#500000', 
              color: '#fff',
              height: '40px',
              textTransform: 'uppercase',
              borderRadius: 0,
              px: 3,
              fontWeight: 400,
              '&:hover': { bgcolor: '#3a0000' } 
            }}
          >
            Search
          </Button>
          <Button 
          variant="outlined" 
          onClick={() => {
            setSearchParams({ projectName: '', irbNum: '' });
            // Let the search run with empty params
            loadProjects(); 
          }}
          sx={{ 
            color: '#500000', 
            bgcolor: '#fff',
            borderColor: '#500000',
            height: '40px',
            textTransform: 'uppercase',
            borderRadius: 0,
            px: 3,
            fontWeight: 400,
            '&:hover': { borderColor: '#500000', bgcolor: 'rgba(80,0,0,0.06)' } 
          }}
        >
          Clear
        </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          "& .MuiDataGrid-cell": {
            paddingLeft: "12px",
            paddingRight: "12px",
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F5F5F5",
          },
        }}
        data-tour-id="tour-project-table"
      >
        <StyledDataGrid
          rows={projects}
          columns={columns}
          getRowId={(row) => row.projectId || Math.random().toString()}
          loading={loading}
          disableRowSelectionOnClick
        />
      </Box>
    </WorkspacePage>
  );
};

export default ProjectManagement;
